<script setup lang="ts">
import getDeliveryPlanVehicleInformation from '@/types/responses/getDeliveryPlanVehicleInformation';
import { formatDateTime } from '@/services/functions';
import DeliveryPlan from "@/types/DeliveryPlan";
import { computed } from 'vue';
import i18n from '@/lang/i18n';

const emit = defineEmits(['update:modelValue']);

const MAX_SELECTABLE_LENGTH = 5;

const props = defineProps<{
  modelValue: (DeliveryPlan | getDeliveryPlanVehicleInformation)[]
  deliveryPlans: DeliveryPlan[]
}>();

const modelValue = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit("update:modelValue", value);
  },
});

function addDeliveryPlan(deliveryPlan: DeliveryPlan) {
  if (isMaxSelected.value) {
    return;
  }
  modelValue.value.push(deliveryPlan);
  modelValue.value = [...modelValue.value].sort((a, b) => {
    const dateA = new Date(a.leaving_warehouse_date_time);
    const dateB = new Date(b.leaving_warehouse_date_time);
    return dateA.getTime() - dateB.getTime();
  });
}

const isSelected = computed(() => (deliveryPlan: DeliveryPlan) => {
  return props.modelValue.some((plan) => plan.delivery_plan_id === deliveryPlan.delivery_plan_id);
})

const isMaxSelected = computed(() => {
  return props.modelValue.length >= MAX_SELECTABLE_LENGTH;
})

function changeFormat(dateTime: Date | string) {
  return typeof dateTime === 'string' ? formatDateTime(new Date(dateTime)) : formatDateTime(dateTime);
}
</script>
<template>
  <tbody>
    <template
      v-for="deliveryPlan in deliveryPlans"
      :key="deliveryPlan.delivery_plan_id"
    >
      <tr>
        <td>
          <span
            class="w-100 mx-auto d-flex justify-content-center"
          >
            {{ deliveryPlan.delivery_plan_id }}
          </span>
        </td>
        <td>
          <span
            class="w-100 mx-auto d-flex justify-content-center"
          >
            {{ deliveryPlan.car_number }}
          </span>
        </td>
        <td>
          <span
            class="w-100 mx-auto d-flex justify-content-center"
          >
            {{ changeFormat(deliveryPlan.leaving_warehouse_date_time) }}
          </span>
        </td>
        <td>
          <span
            class="w-100 mx-auto d-flex justify-content-center"
          >
            {{ changeFormat(deliveryPlan.returning_warehouse_date_time) }}
          </span>
        </td>
        <td>
          <div
            v-if="isSelected(deliveryPlan)"
            class="d-flex justify-content-center text-green"
          >
            <span
              class="material-symbols-outlined"
            >
              check
            </span>
          </div>
          <v-btn
            v-else
            variant="text"
            class="d-flex justify-content-center ml-2"
            :class="isMaxSelected ? 'text-grey-lighten-1' : 'text-blue'"
            @click="addDeliveryPlan(deliveryPlan)"
          >
            <span class="material-symbols-outlined">
              add_circle
            </span>
            <v-tooltip
              v-if="isMaxSelected"
              activator="parent"
            >
              {{ i18n.global.t(`pages.dailyReports.combineForm.maxSelectedMessage`) }}
            </v-tooltip>
          </v-btn>
        </td>
      </tr>
    </template>
  </tbody>
</template>
